import { inject, Injectable } from '@angular/core';
import {
  ColDef,
  CellClickedEvent,
  CheckboxSelectionCallbackParams,
  EditableCallbackParams,
  ICellRendererParams,
  ValueGetterParams,
  CellClassParams,
  GridApi,
} from 'ag-grid-community';
import { Observable } from 'rxjs';

import { DateFormatService } from '../date-format.service';
import { FormatService } from '../format.service';
import { LanguageService } from '../language.service';
import { MenuService } from '../menu.service';
import { UtilityService } from '../utility.service';
import { AggFunc, BooleanStrings } from '../../constants';
import { doNothing } from '../../helpers';
import { GridFieldOptions, IdDescription } from '../../models';
import { ButtonRendererComponent } from '../../components/grids/cell-renderers/button-renderer/button-renderer.component';
import { CheckboxRendererComponent } from '../../components/grids/cell-renderers/checkbox-renderer/checkbox-renderer.component';
import { DateFilterComponent } from '../../components/grids/filters/date-filter/date-filter.component';
import { DateRendererComponent } from '../../components/grids/cell-renderers/date-renderer/date-renderer.component';
import { ImageRendererComponent } from '../../components/grids/cell-renderers/image-renderer/image-renderer.component';
import { SelectRendererComponent } from '../../components/grids/cell-renderers/select-renderer/select-renderer.component';

@Injectable({
  providedIn: 'root',
})
export class GridService {
  protected readonly formatService = inject(FormatService);
  protected readonly languageService = inject(LanguageService);
  protected readonly menuService = inject(MenuService);
  protected readonly dateFormatService = inject(DateFormatService);
  protected readonly utilityService = inject(UtilityService);

  components: any = {
    agDateInput: DateFilterComponent,
    buttonRenderer: ButtonRendererComponent,
    checkboxRenderer: CheckboxRendererComponent,
    dateRenderer: DateRendererComponent,
    imageRenderer: ImageRendererComponent,
    selectRenderer: SelectRendererComponent,
  };

  button<T>(
    onClick: (model: T) => void,
    header: string,
    field: string,
    className: string,
    iconClass: string | ((data: T) => string),
    show?: (data: T) => boolean,
    options?: GridFieldOptions,
    buttonText?: string,
    disabled?: (data: T) => boolean,
  ): ColDef {
    return {
      headerName: header,
      field,
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'buttonRenderer',
      cellRendererParams: {
        className,
        iconClass,
        show,
        onClick,
        buttonText,
        disabled,
      },
      width: 40,
      filter: false,
      sortable: false,
      suppressMenu: true,
      onCellClicked: doNothing,
      ...options,
    };
  }

  commentButton(onClick: (model: any) => void): ColDef {
    return this.button(onClick, '', 'comment', 'comment', (model: any) =>
      !model?.commentsExist ? 'far fa-comment' : 'fa fa-comment',
    );
  }

  calculation(field: string, headerName: string, calculation: (model: any) => any, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      calculation,
      valueGetter: (params: ValueGetterParams) => (params.data[field] ? params.data[field] : calculation(params.data)),
      cellRenderer: GridService.aggregateCellRenderer,
    } as ColDef;
  }

  checkbox(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => editable && editable(params.data);
    return {
      ...this.text(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'checkboxRenderer',
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
      filterValueGetter: (params: ValueGetterParams) =>
        params.data[field] ? BooleanStrings.translatedYes : BooleanStrings.translatedNo,
    } as ColDef;
  }

  checkboxRowSelect(
    field: string,
    headerName: string,
    showSelectAll?: boolean | ((params: any) => boolean),
    showCheckbox?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef[] {
    return this.menuService.isReadOnly
      ? [this.checkbox('selected', ''), this.text(field, headerName)]
      : [
          {
            ...this.text(field, headerName, options),
            checkboxSelection: (params: CheckboxSelectionCallbackParams) =>
              showCheckbox ? showCheckbox(params.data) : true,
            headerCheckboxSelection: showSelectAll,
            headerCheckboxSelectionFilteredOnly: !!showSelectAll,
          } as ColDef,
        ];
  }

  clickableField(
    field: string,
    headerName: string,
    clicked?: (model: any) => void,
    clickable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data?.isAggregate && (!clickable || clickable(params.data));
    return {
      ...this.text(field, headerName, options),
      cellRenderer: GridService.aggregateCellRenderer,
      onCellClicked: (params: CellClickedEvent) => (canEdit(params) && clicked ? clicked(params.data) : undefined),
      cellStyle: (params) =>
        canEdit(params)
          ? { color: 'blue', cursor: 'pointer', 'text-decoration': 'underline' }
          : { cursor: 'default', 'text-decoration': 'none' },
    };
  }

  clickableNumberFormatField(
    field: string,
    headerName: string,
    clicked?: (model: any) => void,
    clickable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data?.isAggregate && (!clickable || clickable(params.data));
    return {
      ...this.numberFormat(field, headerName, options),
      onCellClicked: (params: CellClickedEvent) => (canEdit(params) && clicked ? clicked(params.data) : undefined),
      cellStyle: (params) =>
        canEdit(params)
          ? { color: 'blue', cursor: 'pointer', 'text-decoration': 'underline' }
          : { cursor: 'default', 'text-decoration': 'none' },
    };
  }

  date(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      comparator: this.dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: this.dateComparator,
        buttons: ['reset'],
      },
      valueGetter: (params: ValueGetterParams) => this.formatService.date(params.data ? params.data[field] : undefined),
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: params.data ? this.formatService.date(params.data[field]) : params.value,
        }),
      cellStyle: { overflow: 'visible' },
    };
  }

  dateTime(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.date(field, headerName, options),
      valueGetter: (params: ValueGetterParams) =>
        params.data ? this.formatService.dateTime(params.data[field]) : undefined,
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: params.data ? this.formatService.dateTime(params.data[field]) : params.value,
        }),
    };
  }

  editableDate(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data?.isAggregate && (!editable || editable(params.data));
    if (this.menuService.isReadOnly) {
      return this.date(field, headerName, options);
    }

    return {
      ...this.date(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'dateRenderer',
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
    };
  }

  editableField(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
    cellStyle?: (params: CellClassParams) => any,
    regex?: RegExp,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    const canEdit = (params) => !params.data?.isAggregate && (!editable || editable(params.data));
    return {
      ...this.text(field, headerName, options),
      cellStyle: (params) => ({
        ...(canEdit(params) ? { color: 'blue', cursor: 'text' } : { cursor: 'default' }),
        ...(cellStyle ? cellStyle(params) : {}),
      }),
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
      singleClickEdit: true,
      valueSetter: regex
        ? (params) => {
            if (regex.test(params.newValue ?? '')) {
              params.data[field] = params.newValue;
              return true;
            } else {
              return false;
            }
          }
        : undefined,
    };
  }

  editableNumberFormat(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.numberFormat(field, headerName, options);
    }

    return {
      ...this.editableField(field, headerName, edited, editable, options),
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: this.formatService.number(params.data?.[field] ?? params.value, options?.decimalPlaces),
        }),
    };
  }

  image(field: string, headerName: string, width = 200, height = 150, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'imageRenderer',
      cellRendererParams: {
        width,
        height,
      },
      width,
      autoHeight: true,
      filter: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      ...options,
    };
  }

  number(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
      },
    };
  }

  currency(currencyField: string, field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: this.formatService.currency(
            params.data?.[field] ?? params.value,
            params.data?.[currencyField] ?? '',
            options?.decimalPlaces,
          ),
        }),
      cellStyle: (params: CellClassParams) => (options?.colourNegatives && params.value < 0 ? { color: 'red' } : {}),
      ...{ currencyField },
    };
  }

  numberFormat(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: (params: ICellRendererParams) => {
        let value = params.data?.[field] ?? params.value;
        if (options?.negateValue) {
          value = isNaN(+value) ? 0 : +value * -1;
        }
        return GridService.aggregateCellRenderer({
          ...params,
          value:
            options?.zeroOrNegativeMessageField && params.data[options?.zeroOrNegativeMessageField] && value <= 0
              ? (params.data[options?.zeroOrNegativeMessageField] ?? $localize`Out of stock`)
              : this.formatService.number(value, options?.decimalPlaces),
        });
      },
      cellStyle: (params) => (options?.colourNegatives && params.value < 0 ? { color: 'red' } : {}),
    };
  }

  rowNumber(options?: GridFieldOptions): ColDef {
    return {
      valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
      maxWidth: 40,
      ...options,
    } as ColDef;
  }

  select(
    field: string,
    headerName: string,
    selectOptions?: Observable<IdDescription[]> | IdDescription[],
    options?: GridFieldOptions,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'selectRenderer',
      cellRendererParams: {
        options: this.utilityService.mapSelectOptions(selectOptions),
      },
    };
  }

  text(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      field,
      headerName: this.languageService.translate(headerName, options?.abbreviatedHeaderName),
      width: 200,
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: GridService.aggregateCellRenderer,
      ...options,
    } as ColDef;
  }

  textEllipsis(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellStyle: { overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' },
    } as ColDef;
  }

  setCurrencyTotalColumn(
    gridApi: GridApi,
    columnDefs: ColDef[],
    symbol: string,
    options?: {
      field?: string;
      appendIfMissing?: boolean;
      extra?: Partial<ColDef>;
    },
  ): ColDef[] {
    const field = options?.field ?? 'sterlingTotal';

    const built = {
      ...this.numberFormat(field, $localize`Total (${symbol})`, { aggFunc: AggFunc.sum }),
      ...(options?.extra ?? {}),
    } as ColDef;

    const next = columnDefs.slice();
    const idx = next.findIndex((c) => c.field === field);

    if (idx !== -1) next[idx] = built;
    else if (options?.appendIfMissing) next.push(built);

    gridApi.setColumnDefs(next);
    return next;
  }

  private dateComparator = (filterLocalDateAtMidnight: string | Date, cellValue: string | Date) => {
    const cellDate = this.toDate(cellValue);
    const filterDate = this.toDate(filterLocalDateAtMidnight);

    if (!cellDate || !filterDate) {
      return -1;
    }

    if (filterDate.getTime() === cellDate.getTime()) {
      return 0;
    }

    if (cellDate < filterDate) {
      return -1;
    }

    if (cellDate > filterDate) {
      return 1;
    }

    return 0;
  };

  private toDate(date: string | Date): Date {
    if (date === undefined) {
      return undefined;
    }

    if (date instanceof Date) {
      return date;
    }

    return this.dateFormatService.dateFromString(date);
  }

  private static aggregateCellRenderer(params: ICellRendererParams) {
    if (!params.value && params.value !== 0) {
      return '';
    }
    if (params.data?.isAggregate) {
      return params.data[params.colDef.field] ? `<strong>${params.value}</strong>` : '';
    }

    const aggFunc = params.colDef.aggFunc;
    if (params.node.group) {
      switch (aggFunc) {
        case AggFunc.avg:
        case AggFunc.sum:
        case AggFunc.sumInteger:
        case AggFunc.count:
          return `<strong>${params.value}</strong>`;
        default:
          return params.value;
      }
    }

    switch (aggFunc) {
      case AggFunc.first:
      case AggFunc.last:
      case AggFunc.min:
      case AggFunc.max:
        return '';
      default:
        return params.value;
    }
  }
}
